/**
 * Company: SPHMedia
 * Description: Full display of social media links
 */

import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { socialLinks } from "@components/Footer/constant";
import {
  faFacebookF,
  faLinkedinIn,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

type SocialMediaLinksPopoverProps = {
  rootClassName?: string;
};

/**
 * SocialMediaLinksPopover
 * Description: Sub component of the social links in the header.
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
const SocialMediaLinksPopover = (
  { rootClassName = "" }: SocialMediaLinksPopoverProps,
  ref: ForwardedRef<HTMLDivElement>
): ReactElement => {
  const [currentMailTitle, setCurrentMailTitle] = useState("");
  // Add component logic and state here
  const socialLinkClass =
    "w-8 h-8 flex justify-center items-center cursor-pointer bg-black rounded-pill";
  const socialMediaObjects = [
    {
      media: "facebook",
      href: socialLinks.fbBlack?.link,
      icon: faFacebookF,
    },
    {
      media: "twitter",
      href: socialLinks.twitter?.link,
      icon: faXTwitter,
    },
    {
      media: "whatsapp",
      href: socialLinks.whatsapp?.link,
      icon: faWhatsapp,
    },
    {
      media: "linkedIn",
      href: socialLinks.linkedin?.link,
      icon: faLinkedinIn,
    },
    {
      media: "telegram",
      href: socialLinks.telegram?.link,
      icon: faPaperPlane,
    },
    {
      media: "mail",
      href: currentMailTitle,
      icon: faEnvelope,
    },
  ];

  useEffect(() => {
    const currentTitle = document.title;
    const mailBuilder = `mailto:?subject=sharing from the business times&body=${encodeURIComponent(
      currentTitle
    )}%20https://www.businesstimes.com.sg/${window.location.pathname}`;

    setCurrentMailTitle(mailBuilder);
  }, []);

  return (
    <>
      <div
        className={cx(
          "grid grid-cols-2 gap-x-8 gap-y-2 rounded border border-gray-275 bg-gray-150 px-4 py-3",
          rootClassName
        )}
        ref={ref}
      >
        {socialMediaObjects.map((social, index) => (
          <a
            className="transition duration-300 hover:opacity-50"
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            key={index}
          >
            <div className="flex flex-row items-center gap-2">
              <div className={socialLinkClass}>
                <FontAwesomeIcon icon={social.icon} />
              </div>
              <span className="inline-block align-middle font-poppins capitalize text-black">
                {social.media}
              </span>
            </div>
          </a>
        ))}
      </div>
    </>
  );
};

export default forwardRef(SocialMediaLinksPopover);
